import { useQuery } from "@tanstack/react-query";
import { useDataProvider, useTranslate } from "ra-core";
import { useEffect, useState } from "react";

import { cn } from "@/lib/utils";

import type { NevoraDataProvider } from "../datos/dataProvider";
import { agruparHuecos } from "../dominio/huecos";

/**
 * 41 (Robert, 23-24/10/2026): elegir la hora de la consultoría entre las libres. La base da las horas en
 * las que al menos una persona puede hacerla (su horario, sus otras consultorías y su Google); quién la
 * hace lo decide al agendar. Sustituye al enlace de reservas cuando el calendario propio está encendido.
 */
export const SelectorHuecos = ({
  valor,
  cambiar,
}: {
  /** Instante ISO elegido, o vacío. */
  valor: string;
  cambiar: (iso: string) => void;
}) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider<NevoraDataProvider>();
  const { data, isPending, isError } = useQuery({
    queryKey: ["nevora", "huecos"],
    queryFn: () => dataProvider.huecosConsultoria(),
    staleTime: 30_000,
  });
  const dias = agruparHuecos(data ?? []);
  const [dia, setDia] = useState<string | null>(null);
  useEffect(() => {
    if (!dia && dias.length) setDia(dias[0].dia);
  }, [dia, dias]);
  const elegido = dias.find((d) => d.dia === dia) ?? dias[0];

  if (isPending) {
    return (
      <p className="text-sm text-muted-foreground" role="status">
        {translate("nevora.agenda_propia.cargando")}
      </p>
    );
  }
  if (isError) {
    return (
      <p className="text-sm text-destructive">
        {translate("nevora.agenda_propia.error")}
      </p>
    );
  }
  if (!dias.length) {
    return (
      <p className="text-sm text-amber-800 dark:text-amber-300" role="alert">
        {translate("nevora.agenda_propia.sin_huecos")}
      </p>
    );
  }
  const etiquetaDia = (d: string) =>
    new Date(`${d}T12:00:00`).toLocaleDateString("es-ES", {
      weekday: "short",
      day: "numeric",
      month: "short",
    });
  return (
    <div className="space-y-2" data-testid="selector-huecos">
      <div className="flex gap-1.5 overflow-x-auto pb-1" role="tablist">
        {dias.map((d) => (
          <button
            key={d.dia}
            type="button"
            role="tab"
            aria-selected={d.dia === elegido?.dia}
            onClick={() => setDia(d.dia)}
            className={cn(
              "shrink-0 rounded-md border px-2.5 py-1.5 text-sm first-letter:uppercase",
              d.dia === elegido?.dia
                ? "border-foreground bg-accent font-medium"
                : "border-border bg-card hover:bg-muted",
            )}
          >
            {etiquetaDia(d.dia)}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5" data-testid="horas-huecos">
        {elegido?.horas.map((h) => (
          <button
            key={h.iso}
            type="button"
            aria-pressed={h.iso === valor}
            onClick={() => cambiar(h.iso)}
            className={cn(
              "rounded-md border px-3 py-1.5 text-sm tabular-nums",
              h.iso === valor
                ? "border-[#3D8BD4] bg-[#3D8BD4]/10 font-medium"
                : "border-border bg-card hover:bg-muted",
            )}
          >
            {h.hora}
          </button>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        {translate("nevora.agenda_propia.ayuda_setter")}
      </p>
    </div>
  );
};
